import React, { useState, useRef } from "react";
import {
    Box,
    Typography,
    Avatar,
    Grid,
    useTheme,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Collapse,
    Switch,
    FormControlLabel,
    Slider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    alpha,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    // InputAdornment,
} from "@mui/material";
import Barcode from "react-barcode";
import QRCode from "react-qr-code";
import {
    Settings as SettingsIcon,
    Print as PrintIcon,
    FormatSize as FormatSizeIcon,
    ExpandMore as ExpandMoreIcon,
    Person as PersonIcon,
    Work as WorkIcon,
    Info as InfoIcon,
    Preview as PreviewIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Close as CloseIcon,
    QrCodeScanner as QrCodeScannerIcon,
    ColorLens as ColorLensIcon,
    Crop as CropIcon,
    // Height as HeightIcon,
    // Width as WidthIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { printHtml } from "../../utils/htmlToPrint";

// Utility: Get contrast color for text
const getContrastColor = (hexColor) => {
    const color = hexColor.replace("#", "");
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#ffffff";
};

// ─── Styled Components ──────────────────────────────────────────────
const PVCInfoRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    padding: "1px 0",
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
    "&:last-child": { borderBottom: "none" },
}));

const CreativeCardWrapper = styled(Box)(({ theme, cardColor, isLandscape, cardSize }) => {
    let width = isLandscape ? 380 : 320;
    let height = isLandscape ? 480 : 470;

    // Adjust size based on card size selection
    if (cardSize === "SQUARE") {
        width = isLandscape ? 350 : 320;
        height = isLandscape ? 350 : 320;
    } else if (cardSize === "CR80_ALT") {
        width = isLandscape ? 400 : 340;
        height = isLandscape ? 500 : 470;
    } else if (cardSize === "LARGE") {
        width = isLandscape ? 420 : 360;
        height = isLandscape ? 520 : 500;
    }

    return {
        width,
        height,
        backgroundColor: "white",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
        position: "relative",
        fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        border: `2px solid ${cardColor}`,
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 30px 60px rgba(0,0,0,0.25)",
        },
    };
});

const CreativeHeader = styled(Box)(({ color, fontcolor }) => ({
    height: 140,
    background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.85)})`,
    color: fontcolor || "white",
    textAlign: "center",
    // paddingTop: 8,
    fontWeight: 700,
    position: "relative",
    overflow: "hidden",
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background:
            "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
        opacity: 0.2,
    },
}));

const CreativeCurve = styled(Box)(({ color }) => ({
    position: "absolute",
    top: 110,
    width: "100%",
    height: 40,
    backgroundColor: color,
    borderTopLeftRadius: "100% 100%",
    borderTopRightRadius: "100% 100%",
    opacity: 0.9,
}));

const DiagonalRibbon = styled(Box)(({ color, fontcolor }) => ({
    backgroundColor: color,
    color: fontcolor || "white",
    display: "inline-block",
    padding: "4px 12px",
    transform: "skewX(-20deg)",
    borderRadius: "4px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
}));

const RoleBlock = styled(Box)(({ color, fontcolor }) => ({
    backgroundColor: color,
    color: fontcolor || "white",
    marginLeft:"5px",
    padding: "4px 12px",
    transform: "skewX(-20deg)",
    borderRadius: "4px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: "inline-block",
}));

const ControlsPanel = styled(Paper)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: 1.5,
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "1200px",
    margin: "auto",
    mb: 3,
    p: 2,
    borderRadius: 16,
    background: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: "blur(20px)",
    "@media print": { display: "none" },
}));

// ─── Field Configuration ────────────────────────────────────────────

const FIELD_GROUPS = {
    personal: {
        label: "Personal Information",
        icon: <PersonIcon />,
        fields: [
            { key: "firstName", label: "First Name" },
            { key: "middleName", label: "Middle Name" },
            { key: "lastName", label: "Last Name" },
            { key: "dateOfBirth", label: "Date of Birth" },
        ],
    },
    contact: {
        label: "Contact Details",
        icon: <InfoIcon />,
        fields: [
            { key: "phone", label: "Phone" },
            { key: "email", label: "Email" },
            { key: "addressDTO", label: "Address" },
        ],
    },
    professional: {
        label: "Professional Details",
        icon: <WorkIcon />,
        fields: [
            { key: "employeeCode", label: "Employee Code" },
            { key: "specialization", label: "Specialization" },
            { key: "experience", label: "Experience" },
            { key: "qualification", label: "Qualification" },
            { key: "degree", label: "Degree" },
            { key: "branch", label: "Branch" },
        ],
    },
};

const DEFAULT_VISIBLE_FIELDS = {
    firstName: true,
    middleName: false,
    lastName: true,
    dateOfBirth: false,
    phone: true,
    email: false,
    addressDTO: true,
    employeeCode: true,
    specialization: true,
    experience: false,
    qualification: false,
    degree: false,
    branch: false,
};

// ─── Card Size Options ─────────────────────────────────────────────

const CARD_SIZES = [
    { value: "CR80", label: "CR-80 (Standard)" },
    { value: "CR80_ALT", label: "CR-80 (Alternate)" },
    { value: "SQUARE", label: "Square" },
    { value: "LARGE", label: "Large" },
];

const CARD_STYLES = [
    { value: "modern", label: "Modern" },
    { value: "classic", label: "Classic" },
    { value: "minimal", label: "Minimal" },
    { value: "premium", label: "Premium" },
];

// ─── Main Component ──────────────────────────────────────────────────

export default function CreativeIDCard({ data, school, colors = {}, code: initialCode = "QRCode" }) {
    const theme = useTheme();
    const items = Array.isArray(data) ? data : [];
    const cardRef = useRef(null);

    // ── State ──
    const [showSettings, setShowSettings] = useState(false);
    const [cardColor, setCardColor] = useState(colors?.default || theme.palette.primary.main);
    const [isLandscape, setIsLandscape] = useState(false);
    const [cardScale, setCardScale] = useState(1);
    const [code, setCode] = useState(initialCode);
    const [showBarcode, setShowBarcode] = useState(true);
    const [showPhoto, setShowPhoto] = useState(true);
    const [showRole, setShowRole] = useState(true);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [showShadow, setShowShadow] = useState(true);
    const [autoHeight, setAutoHeight] = useState(true);

    const [fontcolor, setFontcolor] = useState("#FFFFFF");
    const [fontSize, setFontSize] = useState(12);
    const [showWatermark, setShowWatermark] = useState(true);
    const [cardOpacity, setCardOpacity] = useState(1);
    const [showBorder, setShowBorder] = useState(true);
    //   const [showLogo, setShowLogo] = useState(true);
    //   const [showSchoolName, setShowSchoolName] = useState(true);

    // PVC Card Settings
    const [cardSize, setCardSize] = useState("CR80");
    const [cardStyle, setCardStyle] = useState("modern");

    const [visibleFields, setVisibleFields] = useState(DEFAULT_VISIBLE_FIELDS);

    // ── Helpers ──
    const getCardColor = (emp) => colors[emp.id] || colors.default || cardColor;
    const getFontColor = (emp) => fontcolor || getContrastColor(getCardColor(emp));
    const getDarkColor = (emp) => {
        const color = getCardColor(emp);
        return alpha(color, 0.15);
    };

    const formatValue = (emp, key) => {
        const val = emp[key];
        if (val === null || val === undefined) return "N/A";
        if (["dateOfBirth", "joiningDate"].includes(key)) {
            return new Date(val).toLocaleDateString("en-GB");
        }
        if (key === "addressDTO") {
            const addr = val;
            return [addr?.addressLine1, addr?.city, addr?.pincode, addr?.state].filter(Boolean).join(", ");
        }
        return String(val);
    };

    const toggleField = (key) =>
        setVisibleFields((prev) => ({ ...prev, [key]: !prev[key] }));

    const toggleGroup = (groupKey, value) => {
        const newState = {};
        FIELD_GROUPS[groupKey].fields.forEach((f) => (newState[f.key] = value));
        setVisibleFields((prev) => ({ ...prev, ...newState }));
    };

    const isGroupVisible = (groupKey) =>
        FIELD_GROUPS[groupKey].fields.some((f) => visibleFields[f.key]);

    // ── Actions ──
    const toggleLandscape = () => setIsLandscape((v) => !v);

    const handlePrint = () => {
       if (selectedCard?.id) {
             printHtml(selectedCard?.id);
           } else {
             printHtml("creative-cards-container");
           }
           window.location.reload();
    };

    const handleDownloadPDF = async () => {
        const el = document.getElementById("creative-cards-container");
        if (!el) return;
        try {
            const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#fff" });
            const img = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const w = 210;
            const h = (canvas.height * w) / canvas.width;
            pdf.addImage(img, "PNG", 0, 0, w, h);
            pdf.save("creative-id-cards.pdf");
        } catch (err) {
            console.error("PDF error:", err);
        }
    };

    const handlePreview = (emp) => {
        setSelectedCard(emp);
        setPreviewDialog(true);
    };

    // ── Render Card ──
    const renderIDCard = (emp, index) => {
        const color = getCardColor(emp);
        const fontColor = getFontColor(emp);
        const darkColor = getDarkColor(emp);
        const fullName = [emp.firstName, emp.lastName].filter(Boolean).join(" ");
        const role = emp.experience < 1 ? "Teacher" : `Senior ${emp.specialization || ""} Teacher`;

        const infoFields = [
            { key: "employeeCode", label: "ID" },
            { key: "middleName", label: "Father/Husband" },
            { key: "phone", label: "Phone" },
            { key: "email", label: "Email" },
            { key: "specialization", label: "Specialization" },
            { key: "experience", label: "Experience" },
            { key: "qualification", label: "Qualification" },
            { key: "degree", label: "Degree" },
            { key: "dateOfBirth", label: "DOB" },
            { key: "addressDTO", label: "Address" },
            { key: "branch", label: "Branch" },
        ].filter((f) => visibleFields[f.key]);

        return (
            <CreativeCardWrapper
                key={emp.id || index}
                cardColor={showBorder ? color : "transparent"}
                isLandscape={isLandscape}
                cardSize={cardSize}
                sx={{
                    transform: `scale(${cardScale})`,
                    opacity: cardOpacity,
                    boxShadow: showShadow ? "0 20px 50px rgba(0,0,0,0.15)" : "none",
                    "& .MuiTypography-root": { fontSize: `${fontSize}px` },
                    ...(cardStyle === "classic" && {
                        borderRadius: "8px",
                        borderWidth: "3px",
                    }),
                    ...(cardStyle === "minimal" && {
                        boxShadow: "none",
                        borderWidth: "1px",
                    }),
                    ...(cardStyle === "premium" && {
                        borderRadius: "20px",
                        boxShadow: "0 30px 60px rgba(0,0,0,0.3)",
                        borderWidth: "3px",
                    }),
                }}
                ref={cardRef}
            >
                {/* TOP HEADER */}
                <CreativeHeader color={color} fontcolor={fontColor}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            p: 1,
                            gap: 1.5,
                            position: "relative",
                            zIndex: 1,
                        }}
                    >

                        <Avatar
                            src={school?.logoUrl || null}
                            alt="School Logo"
                            sx={{
                                width: isLandscape ? 70 : 80,
                                height: isLandscape ? 70 : 80,
                                borderRadius: 2,
                                border: "2px solid rgba(255,255,255,0.3)",
                                bgcolor: "white",
                                p: 0.5,
                            }}
                        />

                        <Box sx={{ color: fontColor, textAlign: "center", flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                                {school?.schoolName || "School Name"}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2, fontSize: isLandscape ? "0.55rem" : "0.6rem", opacity: 0.9 }}>
                                {[school?.addressDTO?.addressLine1, school?.addressDTO?.city, school?.addressDTO?.pincode, school?.addressDTO?.state]
                                    .filter(Boolean)
                                    .join(", ")}
                                {school?.contactPhone && ` | Tel: ${school.contactPhone}`}
                            </Typography>
                        </Box>
                    </Box>
                </CreativeHeader>

                {/* CURVE STRIPE */}
                <CreativeCurve color={darkColor} />

                {/* PHOTO */}
                {showPhoto && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "82px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            zIndex: 2,
                        }}
                    >
                        <Avatar
                            src={emp.photo || null}
                            alt={fullName}
                            sx={{
                                width: isLandscape ? 100 : 100,
                                height: isLandscape ? 100 : 100,
                                border: `5px solid ${color}`,
                                boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                            }}
                        >
                            {fullName?.charAt(0) || "S"}
                        </Avatar>
                    </Box>
                )}

                {/* CONTENT */}
                <Box
                    sx={{
                        mt: showPhoto ? (isLandscape ? 9 : 9) : 2,
                        px: 2,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        {/* DIAGONAL NAME RIBBON */}
                        <Box sx={{ display: "flex", justifyContent: "flex-start"}}>
                            <DiagonalRibbon color={color} fontcolor={fontColor}>
                                <Typography sx={{ transform: "skewX(20deg)", fontWeight: 600, fontSize: isLandscape ? "0.8rem" : "0.9rem" }}>
                                    {fullName || "Employee Name"}
                                </Typography>
                            </DiagonalRibbon>
                        </Box>

                        {/* ROLE RIGHT BLOCK */}
                        {showRole && (
                            <Box sx={{ display: "flex", justifyContent: "flex-end"}}>
                                <RoleBlock color={color} fontcolor={fontColor}>
                                    <Typography sx={{ transform: "skewX(20deg)", fontWeight: 600, fontSize: isLandscape ? "0.75rem" : "0.85rem" }}>
                                        {role}
                                    </Typography>
                                </RoleBlock>
                            </Box>
                        )}
                    </Box>
                    {/* DETAILS */}
                    <Box sx={{ textAlign: "left", mt: 1 }}>
                        {infoFields.slice(0, 7).map((field) => (
                            <PVCInfoRow key={field.key}>
                                <Typography variant="caption" fontWeight={600} sx={{ fontSize: isLandscape ? "0.55rem" : "0.6rem" }}>
                                    {field.label}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: isLandscape ? "0.55rem" : "0.6rem", textAlign: "left", maxWidth: "60%" }} noWrap>
                                    {formatValue(emp, field.key)}
                                </Typography>
                            </PVCInfoRow>
                        ))}
                    </Box>
                </Box>

                {/* QR / BARCODE */}
                {showBarcode && (
                    <Box sx={{ position: "absolute", bottom: 8, right: 12, display: "flex", alignItems: "center", gap: 0.5 }}>
                        {code === "QRCode" && (
                            <Box sx={{ bgcolor: "white", p: 0.3, borderRadius: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                                <QRCode value={`${emp.id}#${emp.employeeCode}`} size={45} />
                            </Box>
                        )}
                        {code === "Barcode" && (
                            <Box sx={{ bgcolor: "white", p: 0.3, borderRadius: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                                <Barcode width={1} height={40} value={`${emp.id}#${emp.employeeCode}`} displayValue={false} />
                            </Box>
                        )}
                    </Box>
                )}

                {/* WATERMARK */}
                {showWatermark && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 60,
                            left: 10,
                            opacity: 0.03,
                            fontSize: 48,
                            fontWeight: "bold",
                            color: color,
                            pointerEvents: "none",
                            transform: "rotate(-15deg)",
                        }}
                    >
                        {school?.schoolName?.charAt(0) || "S"}
                    </Box>
                )}
            </CreativeCardWrapper>
        );
    };

    // ── Render ──
    return (
        <Box id="creative-id-cards-root">
            {/* Controls */}
            <ControlsPanel elevation={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
                        <QrCodeScannerIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                        Code:
                    </Typography>
                    <Select
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        size="small"
                        sx={{ minWidth: 100, fontSize: "0.75rem" }}
                    >
                        <MenuItem value="Barcode">Bar code</MenuItem>
                        <MenuItem value="QRCode">QR Code</MenuItem>
                    </Select>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
                        <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                        Font Color:
                    </Typography>
                    <input
                        type="color"
                        value={fontcolor}
                        onChange={(e) => setFontcolor(e.target.value)}
                        style={{ width: 28, height: 28, padding: 0, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
                    />
                </Box>

                <Button
                    size="small"
                    variant={isLandscape ? "contained" : "outlined"}
                    startIcon={<FormatSizeIcon />}
                    onClick={toggleLandscape}
                    sx={{ fontSize: "0.75rem", textTransform: "none" }}
                >
                    {isLandscape ? "Landscape" : "Portrait"}
                </Button>

                <FormControlLabel
                    control={
                        <Switch
                            checked={autoHeight}
                            onChange={(e) => setAutoHeight(e.target.checked)}
                            size="small"
                        />
                    }
                    label={<Typography variant="caption">Auto Height</Typography>}
                />

                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => setShowSettings((v) => !v)}
                    sx={{ fontSize: "0.75rem", textTransform: "none" }}
                >
                    Customize
                </Button>

                <Tooltip title="Print Cards">
                    <IconButton size="small" onClick={handlePrint}>
                        <PrintIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                    <IconButton size="small" onClick={handleDownloadPDF}>
                        <PictureAsPdfIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Preview Card">
                    <IconButton size="small" onClick={() => handlePreview(items[0])}>
                        <PreviewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Toggle Shadow">
                    <IconButton size="small" onClick={() => setShowShadow((v) => !v)} color={showShadow ? "primary" : "default"}>
                        <CropIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </ControlsPanel>

            {/* Settings Panel */}
            <Collapse in={showSettings}>
                <Paper sx={{ maxWidth: "1200px", margin: "auto", mb: 3, p: 2.5, bgcolor: "#f8f9fa", borderRadius: 3, "@media print": { display: "none" } }}>
                    <Grid container spacing={2}>
                        {/* PVC Card Settings */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CropIcon fontSize="small" /> PVC Card Settings
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Card Size</InputLabel>
                                        <Select
                                            value={cardSize}
                                            onChange={(e) => setCardSize(e.target.value)}
                                            label="Card Size"
                                        >
                                            {CARD_SIZES.map((size) => (
                                                <MenuItem key={size.value} value={size.value}>
                                                    {size.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Card Style</InputLabel>
                                        <Select
                                            value={cardStyle}
                                            onChange={(e) => setCardStyle(e.target.value)}
                                            label="Card Style"
                                        >
                                            {CARD_STYLES.map((style) => (
                                                <MenuItem key={style.value} value={style.value}>
                                                    {style.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box>
                                        <Typography variant="caption" display="block" gutterBottom>
                                            Card Scale: {cardScale.toFixed(1)}x
                                        </Typography>
                                        <Slider
                                            value={cardScale}
                                            min={0.8}
                                            max={1.2}
                                            step={0.05}
                                            onChange={(_, v) => setCardScale(v)}
                                            size="small"
                                            sx={{ width: "90%" }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box>
                                        <Typography variant="caption" display="block" gutterBottom>
                                            Font Size: {fontSize}px
                                        </Typography>
                                        <Slider
                                            value={fontSize}
                                            min={10}
                                            max={18}
                                            step={0.5}
                                            onChange={(_, v) => setFontSize(v)}
                                            size="small"
                                            sx={{ width: "90%" }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider />
                        </Grid>

                        {/* Toggle Switches */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Display Options
                            </Typography>
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControlLabel
                                        control={<Switch checked={showBorder} onChange={(e) => setShowBorder(e.target.checked)} size="small" />}
                                        label={<Typography variant="caption">Show Border</Typography>}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControlLabel
                                        control={<Switch checked={showPhoto} onChange={(e) => setShowPhoto(e.target.checked)} size="small" />}
                                        label={<Typography variant="caption">Show Photo</Typography>}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControlLabel
                                        control={<Switch checked={showRole} onChange={(e) => setShowRole(e.target.checked)} size="small" />}
                                        label={<Typography variant="caption">Show Role</Typography>}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControlLabel
                                        control={<Switch checked={showWatermark} onChange={(e) => setShowWatermark(e.target.checked)} size="small" />}
                                        label={<Typography variant="caption">Watermark</Typography>}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControlLabel
                                        control={<Switch checked={showBarcode} onChange={(e) => setShowBarcode(e.target.checked)} size="small" />}
                                        label={<Typography variant="caption">Show QR/BarCode</Typography>}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControlLabel
                                        control={<Switch checked={showShadow} onChange={(e) => setShowShadow(e.target.checked)} size="small" />}
                                        label={<Typography variant="caption">Card Shadow</Typography>}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider />
                        </Grid>

                        {/* Field Visibility */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Field Visibility
                            </Typography>
                            {Object.entries(FIELD_GROUPS).map(([groupKey, group]) => (
                                <Accordion key={groupKey} sx={{ "&:before": { display: "none" }, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {group.icon}
                                            <Typography variant="caption" fontWeight={600}>{group.label}</Typography>
                                            <Chip size="small" label={isGroupVisible(groupKey) ? "Visible" : "Hidden"} color={isGroupVisible(groupKey) ? "success" : "default"} variant="outlined" sx={{ ml: 1, height: 20, fontSize: "0.6rem" }} />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box display="flex" justifyContent="flex-end" mb={1}>
                                            <Button size="small" onClick={() => toggleGroup(groupKey, !group.fields.every((f) => visibleFields[f.key]))} sx={{ fontSize: "0.65rem" }}>
                                                {group.fields.every((f) => visibleFields[f.key]) ? "Hide All" : "Show All"}
                                            </Button>
                                        </Box>
                                        <FormGroup>
                                            <Grid container spacing={0.5}>
                                                {group.fields.map((field) => (
                                                    <Grid item xs={12} sm={6} md={4} key={field.key}>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={visibleFields[field.key] || false} onChange={() => toggleField(field.key)} size="small" color="primary" />}
                                                            label={<Typography variant="caption">{field.label}</Typography>}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </FormGroup>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            {/* Cards Grid */}
            <Box id="creative-cards-container">
                <Grid container spacing={3} justifyContent="center" mt={3}>
                    {items.map((emp, i) => (
                        <Grid item key={emp.id || i} xs={12} sm={6} md={4} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
                            {renderIDCard(emp, i)}
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Preview Dialog */}
            <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 2 } }}>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Card Preview</Typography>
                        <IconButton onClick={() => setPreviewDialog(false)} size="small"><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ display: "flex", justifyContent: "center", py: 3 }} id={selectedCard?.id}>
                    {selectedCard && renderIDCard(selectedCard)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialog(false)}>Close</Button>
                    <Button variant="contained" onClick={handlePrint}><PrintIcon fontSize="small" sx={{ mr: 1 }} /> Print</Button>
                </DialogActions>
            </Dialog>

            {/* Empty State */}
            {items.length === 0 && (
                <Box sx={{ textAlign: "center", py: 8, px: 2, bgcolor: "#f8f9fa", borderRadius: 4 }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>No ID Cards to Display</Typography>
                    <Typography variant="body2" color="textSecondary">Please add employee data to generate ID cards.</Typography>
                </Box>
            )}
        </Box>
    );
}